import { ImageOff } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { sanitizeHighlight } from '@/lib/utils';

import { RatingStars } from './RatingStars';

import type { ProductResponse } from '@kore/shared';

/**
 * Tarjeta del catálogo: imagen · título · SKU · rating (placeholder) ·
 * descripción · precio azul · disponibilidad · acciones.
 */
export function ProductCard({ product }: { product: ProductResponse }): JSX.Element {
  const [imageFailed, setImageFailed] = useState(false);
  const navigate = useNavigate();
  const available = product.stock > 0;

  return (
    <Card className="flex h-full flex-col overflow-hidden transition-shadow hover:shadow-md">
      {/* Imagen */}
      <div className="flex h-44 items-center justify-center bg-muted/50">
        {product.imageUrl && !imageFailed ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <ImageOff className="size-10 text-muted-foreground/50" aria-label="Sin imagen" />
        )}
      </div>

      {/* Información */}
      <CardContent className="flex-1 space-y-2 px-5 pt-4 pb-2">
        {/* `highlight` (ts_headline) resalta el término buscado; sin búsqueda cae al nombre. */}
        {product.highlight ? (
          <h3
            className="font-semibold leading-snug [&_mark]:bg-yellow-200 [&_mark]:text-inherit"
            dangerouslySetInnerHTML={{ __html: sanitizeHighlight(product.highlight) }}
          />
        ) : (
          <h3 className="font-semibold leading-snug">{product.name}</h3>
        )}
        <p className="font-mono text-xs text-muted-foreground">{product.sku}</p>
        <RatingStars productId={product.id} />
        {product.description && (
          <p className="line-clamp-2 text-sm text-muted-foreground">
            {product.description.replace(/<[^>]*>/g, '')}
          </p>
        )}
      </CardContent>

      {/* Footer */}
      <CardFooter className="flex-col items-stretch gap-3 px-5 pt-2 pb-5">
        <div className="flex items-center justify-between">
          <span className="text-xl font-bold text-primary">${product.price.toFixed(2)}</span>
          {available ? (
            <Badge className="bg-success text-success-foreground hover:bg-success">
              {product.stock} disponibles
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-muted-foreground">
              Agotado
            </Badge>
          )}
        </div>
        <Button
          variant="outline"
          className="w-full"
          onClick={() => navigate(`/product/${product.id}`)}
        >
          Ver Detalles
        </Button>
      </CardFooter>
    </Card>
  );
}
